/**
 * Failures of the site's own scripts go to the server log
 * (POST /api/v1/client-errors): uncaught errors, unhandled promise rejections
 * and Content-Security-Policy violations. A few per page at most, each text
 * once. Texts are cut and URLs keep only host and path before they leave the
 * page (the password reset link carries its key in the query). Errors of
 * other origins (browser extensions, Google's scripts) are not ours to fix
 * and are not sent.
 */

import { isAbortError, requestJson } from "./http";

const ENDPOINT = "/api/v1/client-errors";
const REPORT_TIMEOUT_MS = 10_000;
export const MAX_REPORTS_PER_PAGE = 5;
const MESSAGE_MAX = 300;
export const LOCATION_MAX = 200;
// what a browser says about an error in a script of another origin: nothing to act on
const OPAQUE_ERROR = "Script error.";
const URL_WITH_SCHEME = /^[a-z][a-z0-9+.-]*:/i;

export interface ClientErrorReport {
  readonly kind: "error" | "rejection" | "csp";
  readonly message: string;
  readonly source: string;
  readonly line: number;
  readonly page: string;
}

/** Host and path of a URL (no user info, query or fragment); CSP keywords such as "inline" stay as they are. */
export function locationOf(url: string, base: string): string {
  if (url === "") return "";
  if (!URL_WITH_SCHEME.test(url) && !url.startsWith("/")) {
    return url.slice(0, LOCATION_MAX);
  }
  try {
    const parsed = new URL(url, base);
    const sameOrigin = parsed.origin === new URL(base).origin;
    return `${sameOrigin ? "" : parsed.host}${parsed.pathname}`.slice(
      0,
      LOCATION_MAX,
    );
  } catch {
    return "(unreadable URL)";
  }
}

/** Whether a script URL belongs to the page's own origin (an empty URL counts as ours). */
export function isOwnScript(url: string, base: string): boolean {
  if (url === "") return true;
  try {
    return new URL(url, base).origin === new URL(base).origin;
  } catch {
    return false;
  }
}

function describe(reason: unknown): string {
  if (reason instanceof Error) return `${reason.name}: ${reason.message}`;
  return typeof reason === "string" ? reason : "non-Error rejection";
}

function clip(text: string): string {
  return text.replace(/\s+/g, " ").trim().slice(0, MESSAGE_MAX);
}

/** A report function that sends each distinct failure once, at most MAX_REPORTS_PER_PAGE of them, and never throws. */
export function createReporter(
  send: (report: ClientErrorReport) => Promise<unknown>,
): (report: ClientErrorReport) => void {
  const seen = new Set<string>();
  return (report) => {
    const key = `${report.kind}\n${report.message}\n${report.source}`;
    if (seen.size >= MAX_REPORTS_PER_PAGE || seen.has(key)) return;
    seen.add(key);
    send(report).catch(() => {
      // the report is best effort: a failure here must not cause another report
    });
  };
}

export function initErrorReporting(): void {
  const base = window.location.href;
  const page = locationOf(base, base);
  const report = createReporter((body) =>
    requestJson("POST", ENDPOINT, { body, timeoutMs: REPORT_TIMEOUT_MS }),
  );
  window.addEventListener("error", (event) => {
    if (!isOwnScript(event.filename, base)) return;
    const message = event.message || describe(event.error);
    if (message === OPAQUE_ERROR) return;
    report({
      kind: "error",
      message: clip(message),
      source: locationOf(event.filename, base),
      line: event.lineno,
      page,
    });
  });
  window.addEventListener("unhandledrejection", (event) => {
    if (isAbortError(event.reason)) return;
    report({
      kind: "rejection",
      message: clip(describe(event.reason)),
      source: "",
      line: 0,
      page,
    });
  });
  document.addEventListener("securitypolicyviolation", (event) => {
    if (!isOwnScript(event.sourceFile, base)) return;
    report({
      kind: "csp",
      message: clip(`${event.effectiveDirective} blocked`),
      source: locationOf(event.blockedURI, base),
      line: event.lineNumber,
      page,
    });
  });
}
